import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { DropdownValueObject } from './dropdown-value-object';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'conn-dropdown',
  templateUrl: './dropdown.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./dropdown.component.css'],
  standalone: false,
})
export class DropdownComponent implements OnInit, OnChanges {
  private logger = inject(NGXLogger);

  /**
   // Collection of dropdownValues that make up the dropdown list
   */
  @Input() values: DropdownValueObject[];

  /**
   * Make the dropdown disabled to prevent it from being changed
   */
  @Input() disabled = false;

  /**
   * Option to include numbering for each dropdown option
   */
  @Input() numberLabels = false;

  /**
   * Option to make the labels all update case instead of the default title case.
   */
  @Input() upperCaseLabel = false;

  /**
   // Emitter to announce to other listeners that a loadValue change has occurred.
   */
  @Output() valueChange: EventEmitter<DropdownValueObject>;
  /**
   // The current selection of the dropdown item
   */
  @Output() selectedValue: DropdownValueObject;
  /**
   // If we want to preload or assign a loadValue to a dropdown then we can use this.
   */
  @Input() loadValue: DropdownValueObject;
  /**
   // Will the default loadValue for this dropdown be <Select>?  Defaults to false
   */
  @Input() defaultSelect = false;
  /**
   * determines if the dropdown menu is in an error state
   * @type {boolean}
   */
  @Input() isInvalid = false;
  /**
   * If the component isInvalid pass in an optional message that will popup when hovering over the dropdown
   */
  @Input() invalidMessage = 'This value is invalid';
  /**
   * takes in a loadValue if a dropdown menu should be afixed width rathern than 100% width
   */
  @Input() fixedWidth: number;
  /**
   * this is a string of space seperated class names we want to enact on the button in the dropdown
   */
  @Input() largeButtons: boolean;

  /**
   * optional value used when numberLabels is turned on.  Stores the true base 0 index and lets the html
   * add the +1 so that the number appears correct for the user.
   */
  selectedIndex: number;
  className = 'Dropdown.component.ts: ';

  constructor() {
    this.valueChange = new EventEmitter();
  }

  /**
   * This fires off anytime a change is made to any dropdown menu which can be quite frequent however we are
   * only interested in a very particular type of change.  That is when the loadValue gets changed after a dropdown
   * has been initialized.  The load value is used to programatically set currently selected dropdown w/o emitting
   * a change event.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    const hasChanges = !!changes;
    const isFirstTimeLoadChange =
      hasChanges &&
      !changes.firstChange &&
      changes.loadValue &&
      !changes.loadValue.firstChange;
    if (isFirstTimeLoadChange) {
      this.logger.debug(
        `${this.className}: loadValue was changed from ${changes?.loadValue?.previousValue} to ${changes.loadValue.currentValue} assigning to selectedValue and index.`,
      );
      this.selectedValue = changes.loadValue.currentValue;
      this.selectedIndex = this.values.findIndex(
        (value) => value.value === this.selectedValue.value,
      );
    }
  }

  /*Determine if we want <Select> to get added to the data and assigns first time in collection to the selectedValue.*/
  ngOnInit() {
    if (
      this.values &&
      this.defaultSelect &&
      this.values[0].value !== '<Select>'
    ) {
      this.values.unshift(new DropdownValueObject('<Select>'));
    }
    if (this.loadValue) {
      this.selectedValue = this.loadValue;
      this.selectedIndex = this.values.findIndex(
        (value) => value.value === this.selectedValue.value,
      );
    } else if (this.values) {
      this.selectedValue = this.values[0];
      this.selectedIndex = 0;
    } else {
      throw new Error('Dropdown list cannot be empty!');
    }
  }

  /**
   * Called by the dom when a new dropdown menu item has been selected
   * @param value
   * @param selectedIndex
   */
  selectDropdown(value: DropdownValueObject, selectedIndex: number) {
    this.selectedValue = value;
    this.selectedIndex = selectedIndex;
    this.valueChange.emit(value);
  }
}
